import {
  skipToken,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { ValidationRunStatus } from '@erp/shared-types';
import type {
  ValidationRuleStatus,
  ValidationViolationStatus,
} from '@erp/shared-types';
import type {
  CreateValidationRuleInput,
  CreateValidationRuleResponse,
  DeleteValidationRuleResponse,
  DuplicateValidationRuleResponse,
  GetViolationRowDataInput,
  GetViolationRowDataResponse,
  ListValidationRuleSchemasResponse,
  ListValidationRulesResponse,
  ListValidationRunsResponse,
  ListValidationViolationsResponse,
  PreviewValidationRuleInput,
  PreviewValidationRuleResponse,
  RegenerateValidationReviewInput,
  RegenerateValidationReviewResponse,
  RunValidationRuleInput,
  RunValidationRuleResponse,
  SetViolationExemptionInput,
  SetViolationExemptionResponse,
  UpdateValidationRuleInput,
  UpdateValidationRuleResponse,
} from '@erp/rpc';
import { orpc, rpcClient } from '@/api/rpc/client';
import { useToast } from '@/context/ToastContext';

/** What the rules list page filters and pages by. */
export interface ValidationRuleListFilters {
  /** Omit for "every status". */
  status?: ValidationRuleStatus;
  page: number;
  pageSize: number;
}

/**
 * One page of the org's validation rules. Each row already carries its last
 * run and its open violation/review counts — the list never fans out one
 * request per rule.
 */
export function useValidationRules(
  filters: ValidationRuleListFilters,
  enabled = true,
) {
  const query = useQuery({
    ...orpc.inventoryManagement.validationRules.list.queryOptions({
      input: {
        status: filters.status,
        page: filters.page,
        pageSize: filters.pageSize,
      },
    }),
    enabled,
  });

  return {
    ...query,
    rules: (query.data?.rules ??
      []) satisfies ListValidationRulesResponse['rules'],
    total: query.data?.total ?? 0,
  };
}

/** One validation rule, for its detail page. */
export function useValidationRule(id: string | undefined, enabled = true) {
  const query = useQuery({
    ...orpc.inventoryManagement.validationRules.get.queryOptions({
      input: id ? { id } : skipToken,
    }),
    enabled: Boolean(id) && enabled,
  });

  return { ...query, rule: query.data?.rule ?? null };
}

/**
 * The org's scannable logical schemas and their fields — what the rule builder
 * offers as a table to validate and as grouping/consistent fields. The list is
 * schema metadata, so it is cached long and shared by every wizard instance.
 */
export function useValidationRuleSchemas(enabled = true) {
  const query = useQuery({
    ...orpc.inventoryManagement.validationRules.listRuleSchemas.queryOptions({
      input: {},
    }),
    enabled,
    staleTime: 5 * 60 * 1000,
  });

  return {
    ...query,
    schemas: (query.data?.schemas ??
      []) satisfies ListValidationRuleSchemasResponse['schemas'],
  };
}

/**
 * Every list of rules, and the one rule's own detail query when an id is known.
 * A write can change a rule's status or its counts, so both are refreshed.
 */
function useInvalidateValidationRules() {
  const queryClient = useQueryClient();
  return (ruleId?: string) => {
    void queryClient.invalidateQueries({
      queryKey: orpc.inventoryManagement.validationRules.list.key(),
    });
    if (!ruleId) return;
    void queryClient.invalidateQueries({
      queryKey: orpc.inventoryManagement.validationRules.get.queryKey({
        input: { id: ruleId },
      }),
    });
  };
}

/**
 * Create a validation rule. Failures are left to the caller: the rule builder
 * renders them on its save step (the gate the author is standing on), where a
 * toast would say less about which field the server rejected.
 */
export function useCreateValidationRule() {
  const invalidate = useInvalidateValidationRules();
  const { showToast } = useToast();

  return useMutation<
    CreateValidationRuleResponse,
    Error,
    CreateValidationRuleInput
  >({
    mutationFn: (input) =>
      rpcClient.inventoryManagement.validationRules.create(input),
    onSuccess: (data) => {
      invalidate(data.rule.id);
      showToast({
        title: 'Rule saved',
        message: `“${data.rule.name}” was created.`,
        color: 'success',
      });
    },
  });
}

/** Update a validation rule's definition, assignment, or status. */
export function useUpdateValidationRule() {
  const invalidate = useInvalidateValidationRules();
  const { showToast } = useToast();

  return useMutation<
    UpdateValidationRuleResponse,
    Error,
    UpdateValidationRuleInput
  >({
    mutationFn: (input) =>
      rpcClient.inventoryManagement.validationRules.update(input),
    onSuccess: (data) => {
      invalidate(data.rule.id);
      showToast({
        title: 'Rule saved',
        message: `“${data.rule.name}” was updated.`,
        color: 'success',
      });
    },
  });
}

/** Copy a rule as a fresh draft named "Copy of …". */
export function useDuplicateValidationRule() {
  const invalidate = useInvalidateValidationRules();
  const { showToast } = useToast();

  return useMutation<DuplicateValidationRuleResponse, Error, { id: string }>({
    mutationFn: (input) =>
      rpcClient.inventoryManagement.validationRules.duplicate(input),
    onSuccess: (data) => {
      invalidate(data.rule.id);
      showToast({
        title: 'Rule duplicated',
        message: `“${data.rule.name}” is a draft you can edit.`,
        color: 'success',
      });
    },
    // Unlike create/update, a duplicate is not run from a form that can hold
    // the message, so the failure is reported where the click happened.
    onError: (error) => {
      showToast({
        title: 'Error',
        message: error.message || 'Failed to duplicate that rule',
        color: 'error',
      });
    },
  });
}

/** Delete a validation rule. `onDeleted` is where the caller navigates away. */
export function useDeleteValidationRule(onDeleted?: () => void) {
  const invalidate = useInvalidateValidationRules();
  const { showToast } = useToast();

  return useMutation<DeleteValidationRuleResponse, Error, { id: string }>({
    mutationFn: (input) =>
      rpcClient.inventoryManagement.validationRules.delete(input),
    onSuccess: () => {
      invalidate();
      showToast({
        title: 'Rule deleted',
        message: 'The validation rule and its history are gone.',
        color: 'success',
      });
      onDeleted?.();
    },
    onError: (error) => {
      showToast({
        title: 'Error',
        message: error.message || 'Failed to delete that rule',
        color: 'error',
      });
    },
  });
}

/**
 * Evaluate a rule — saved (`ruleId`) or still being authored (`rule`) — and
 * report what it finds without writing anything. No invalidation: a preview
 * changes no server state. Errors are rendered where the preview was asked for.
 */
export function usePreviewValidationRule() {
  return useMutation<
    PreviewValidationRuleResponse,
    Error,
    PreviewValidationRuleInput
  >({
    mutationFn: (input) =>
      rpcClient.inventoryManagement.validationRules.preview(input),
  });
}

// ─── Runs and violations ─────────────────────────────────────────────────────

/** What a rule's run-history table pages by. */
export interface ValidationRunListFilters {
  page: number;
  pageSize: number;
}

/** One page of a rule's run history, newest first. */
export function useValidationRuns(
  ruleId: string | undefined,
  filters: ValidationRunListFilters,
  enabled = true,
) {
  const query = useQuery({
    ...orpc.inventoryManagement.validationRules.listRuns.queryOptions({
      input: ruleId
        ? { ruleId, page: filters.page, pageSize: filters.pageSize }
        : skipToken,
    }),
    enabled: Boolean(ruleId) && enabled,
  });

  return {
    ...query,
    runs: (query.data?.runs ?? []) satisfies ListValidationRunsResponse['runs'],
    total: query.data?.total ?? 0,
  };
}

/**
 * What a violations list asks for. At least one of `ruleId` / `runId` is
 * required by the contract; the query stays idle until one is known. A `runId`
 * means "the violations that run saw", not "the ones it created".
 */
export interface ValidationViolationListFilters {
  ruleId?: string;
  runId?: string;
  status?: ValidationViolationStatus;
  page: number;
  pageSize: number;
}

/** One page of persisted violations. */
export function useValidationViolations(
  filters: ValidationViolationListFilters,
  enabled = true,
) {
  const addressed = Boolean(filters.ruleId ?? filters.runId);
  const query = useQuery({
    ...orpc.inventoryManagement.validationRules.listViolations.queryOptions({
      input: addressed
        ? {
            ruleId: filters.ruleId,
            runId: filters.runId,
            status: filters.status,
            page: filters.page,
            pageSize: filters.pageSize,
          }
        : skipToken,
    }),
    enabled: addressed && enabled,
  });

  return {
    ...query,
    violations: (query.data?.violations ??
      []) satisfies ListValidationViolationsResponse['violations'],
    total: query.data?.total ?? 0,
  };
}

/**
 * Run the rule and stage every open violation into a brand-new review, leaving
 * the previous review untouched — the escape hatch after exemptions (or manual
 * cleanup) have made the open draft stale. The old draft stays so edits made on
 * it can be copied over; it is deleted by hand when done.
 */
export function useRegenerateValidationReview() {
  const invalidateRules = useInvalidateValidationRules();
  const invalidateRuns = useInvalidateValidationRuns();
  const { showToast } = useToast();

  return useMutation<
    RegenerateValidationReviewResponse,
    Error,
    RegenerateValidationReviewInput
  >({
    mutationFn: (input) =>
      rpcClient.inventoryManagement.validationRules.regenerateReview(input),
    onSuccess: (data, input) => {
      invalidateRules(input.ruleId);
      invalidateRuns();

      if (data.status === ValidationRunStatus.Failed) {
        showToast({
          title: 'Run failed',
          // The run's own recorded sentence, rendered verbatim.
          message: data.error ?? 'The run failed without a message.',
          color: 'error',
        });
        return;
      }

      showToast({
        title: 'Review regenerated',
        message: data.changeSetId
          ? 'A fresh review was staged from the current data. The previous review was left as it was — copy anything you need from it, then delete it.'
          : 'Every group agrees (or is exempted), so there was nothing to stage and no review was created.',
        color: 'success',
      });
    },
    onError: (error) => {
      showToast({
        title: 'Error',
        message: error.message || 'Failed to regenerate the review',
        color: 'error',
      });
    },
  });
}

/**
 * Exempt one violation as acceptable, or put it back in play. Rewrites the
 * violation lists (the row moves between the Open and Exempted views) and the
 * rules list, whose open counts no longer include it.
 */
export function useSetViolationExemption() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation<
    SetViolationExemptionResponse,
    Error,
    SetViolationExemptionInput
  >({
    mutationFn: (input) =>
      rpcClient.inventoryManagement.validationRules.setViolationExemption(
        input,
      ),
    onSuccess: (data) => {
      void queryClient.invalidateQueries({
        queryKey: orpc.inventoryManagement.validationRules.listViolations.key(),
      });
      void queryClient.invalidateQueries({
        queryKey: orpc.inventoryManagement.validationRules.list.key(),
      });
      showToast({
        title: data.violation.exemptedAt
          ? 'Violation exempted'
          : 'Exemption removed',
        message: data.violation.exemptedAt
          ? 'Future runs will keep it out of reviews until the exemption is removed.'
          : 'The next run treats it like any other open violation.',
        color: 'success',
      });
    },
    onError: (error) => {
      showToast({
        title: 'Error',
        message: error.message || 'Failed to update that violation',
        color: 'error',
      });
    },
  });
}

/**
 * The complete ingest rows behind one violation card, fetched when the reader
 * expands the card to all columns. Read live — the whole point is seeing the
 * row as it is now — but cached briefly so collapsing and re-expanding a card
 * doesn't re-read the ingest DB.
 */
export function useValidationViolationRowData(
  rows: GetViolationRowDataInput['rows'],
  enabled: boolean,
) {
  const query = useQuery({
    ...orpc.inventoryManagement.validationRules.getViolationRowData.queryOptions(
      { input: { rows } },
    ),
    enabled: enabled && rows.length > 0,
    staleTime: 60 * 1000,
  });

  return {
    ...query,
    fullRows: (query.data?.rows ??
      []) satisfies GetViolationRowDataResponse['rows'],
  };
}

/**
 * A run rewrites the rule's history, its violations, and the counts on the
 * rules list — so all of them are dropped rather than patched.
 */
function useInvalidateValidationRuns() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({
      queryKey: orpc.inventoryManagement.validationRules.listRuns.key(),
    });
    void queryClient.invalidateQueries({
      queryKey: orpc.inventoryManagement.validationRules.listViolations.key(),
    });
    // A run may open or append to a draft review, which the reviews list shows.
    void queryClient.invalidateQueries({
      queryKey: orpc.inventoryManagement.metadataChangeSets.list.key(),
    });
  };
}

/** The sentence a finished run gets reported with. */
function runOutcomeMessage(data: RunValidationRuleResponse): string {
  const flagged = `Flagged ${data.stats.violatingGroups} of ${data.stats.groupsEvaluated} groups — ${data.delta.newViolations} new, ${data.delta.resolvedViolations} resolved.`;
  return data.changeSetId
    ? `${flagged} The proposed corrections were staged into a review.`
    : flagged;
}

/**
 * Run a rule now and wait for the outcome. A run that *fails* still resolves —
 * the run happened, and its recorded `error` is the message worth showing — so
 * the failure is toasted from `onSuccess`, verbatim. Only a rejected request
 * (a paused rule, a run already in flight) reaches `onError`.
 */
export function useRunValidationRuleNow() {
  const invalidateRules = useInvalidateValidationRules();
  const invalidateRuns = useInvalidateValidationRuns();
  const { showToast } = useToast();

  return useMutation<RunValidationRuleResponse, Error, RunValidationRuleInput>({
    mutationFn: (input) =>
      rpcClient.inventoryManagement.validationRules.runNow(input),
    onSuccess: (data, input) => {
      invalidateRules(input.ruleId);
      invalidateRuns();

      if (data.status === ValidationRunStatus.Failed) {
        showToast({
          title: 'Run failed',
          // The run's own recorded sentence, rendered verbatim.
          message: data.error ?? 'The run failed without a message.',
          color: 'error',
        });
        return;
      }

      showToast({
        title: 'Run finished',
        message: runOutcomeMessage(data),
        color: 'success',
      });
    },
    onError: (error) => {
      showToast({
        title: 'Error',
        message: error.message || 'Failed to run that rule',
        color: 'error',
      });
    },
  });
}
